"use client";

import React, { useEffect, useState, useCallback } from "react";
import PromotionalBanner from "../common/PromotionalBanner";
import ProductCardV2 from "./ProductCardV2";
import { toast } from "sonner";
import { fetchTopSelling } from "@/api-service/product";
import { Product, Variant } from "@/packages/types/products";
import QuickViewModal from "../productdetail/QuickViewModal";
import Loader from "../common/LoaderComponent";
import { useFastRouter } from "@/packages/hooks/useFastRouter";
import { Swiper, SwiperSlide } from "swiper/react";
import { Keyboard } from "swiper/modules";
import { Swiper as SwiperType } from "swiper";
import { ChevronLeft, ChevronRight } from "lucide-react";

import "swiper/css";

import { HEADER_V2_DESIGN_SYSTEM } from "../header/v2/designTokens";
import { useResolvedCountryName } from "@/packages/hooks/useResolvedCountryName";

interface TopSellingProduct extends Product {
  variants?: Variant[];
}

interface TopSellingSectionProps {
  showBanner?: boolean;
  bannerImage?: string;
}

const TopSellingWithBanner: React.FC<TopSellingSectionProps> = ({
  showBanner = false,
  bannerImage = "",
}) => {
  const countryName = useResolvedCountryName();
  const [products, setProducts] = useState<TopSellingProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const [bannerImg, setBannerImage] = useState<string>(bannerImage);
  const [bannerLink, setBannerLink] = useState<string>("");
  const [bannerMobileImg, setBannerMobileImg] = useState<string | undefined>(
    undefined
  );

  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  const { fastPush } = useFastRouter();

  useEffect(() => {
    let isMounted = true;
    const loadProducts = async () => {
      try {
        setLoading(true);
        const res = await fetchTopSelling();
        if (isMounted) {
          const fetchedProducts = (res?.data?.products?.products || []) as TopSellingProduct[];
          setProducts(fetchedProducts);
          const promo = res.data?.promoCards?.[0];
          setBannerImage(promo?.imageUrl || bannerImage);
          setBannerMobileImg(promo?.mobileUrl || undefined);
          setBannerLink(promo?.navigationLink || "");
        }
      } catch (error: unknown) {
        console.error("Error fetching top selling products:", error);
        if (isMounted) {
          toast.error("Error fetching top selling products");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    loadProducts();
    return () => {
      isMounted = false;
    };
  }, [bannerImage]);

  const handlePrev = useCallback(() => {
    swiperInstance?.slidePrev();
  }, [swiperInstance]);

  const handleNext = useCallback(() => {
    swiperInstance?.slideNext();
  }, [swiperInstance]);

  const handleSwiperInit = useCallback((swiper: SwiperType) => {
    setSwiperInstance(swiper);
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  }, []);

  const handleSlideChange = useCallback((swiper: SwiperType) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  }, []);

  const handleQuickView = (product: Product) => {
    setSelectedProduct(product);
    setIsQuickViewOpen(true);
  };

  if (!loading && products.length === 0) {
    return null;
  }

  const leftBanner = (
    <div className="w-full md:w-[30%] lg:w-[25%] shrink-0">
      <div className="w-full h-[120px] md:h-auto md:aspect-square relative rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300">
        <PromotionalBanner
          image={bannerImg}
          mobileImage={bannerMobileImg}
          text={""}
          slug={bannerLink}
          count={1}
          className="w-full h-full object-cover"
        />
      </div>
    </div>
  );

  return (
    <section className={`w-full max-w-7xl mx-auto my-8 md:my-12 ${HEADER_V2_DESIGN_SYSTEM.spacing.containerPadding}`}>
      <div className="flex justify-between items-baseline mb-6 md:mb-8">
        <h2 className={`font-semibold tracking-tight ${HEADER_V2_DESIGN_SYSTEM.typography.fontFamilyHeading} ${HEADER_V2_DESIGN_SYSTEM.typography.size20} ${HEADER_V2_DESIGN_SYSTEM.colors.primaryDarkText}`}>
          Top Selling Products{countryName ? ` in ${countryName}` : ""}
        </h2>
        <button
          onClick={() => fastPush(bannerLink || "/shop")}
          className="text-xs md:text-sm font-semibold text-slate-800 underline hover:text-[#D97706] transition-colors"
        >
          See All
        </button>
      </div>

      {loading ? (
        <div className="h-[300px] flex items-center justify-center bg-slate-50/50 rounded-2xl border border-slate-100">
          <Loader variant="spinner" label="Loading products..." size="lg" />
        </div>
      ) : (
        <div className="flex flex-col md:flex-row gap-6 items-stretch w-full">
          {showBanner && leftBanner}

          <div className="relative w-full shrink-0">
            {/* Swiper Slider Wrapper */}
            <div className="relative w-full px-1 py-1">
              <Swiper
                modules={[Keyboard]}
                spaceBetween={8}
                slidesPerView={2.2}
                keyboard={{ enabled: true }}
                onSwiper={handleSwiperInit}
                onSlideChange={handleSlideChange}
                breakpoints={{
                  480: { slidesPerView: 2.2, spaceBetween: 12 },
                  640: { slidesPerView: 3.2, spaceBetween: 16 },
                  1024: { slidesPerView: 5, spaceBetween: 16 },
                }}
                className="w-full !overflow-visible md:!overflow-hidden"
              >
                {products.map((product, index) => (
                  <SwiperSlide key={product.id || index} className="h-auto">
                    <ProductCardV2
                      product={product}
                      onQuickView={() => handleQuickView(product)}
                      className="h-full"
                    />
                  </SwiperSlide>
                ))}
              </Swiper>

              {/* Edge Translucent Navigation Chevrons */}
              {products.length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handlePrev();
                    }}
                    disabled={isBeginning}
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 
                      hidden md:flex items-center justify-center w-8 h-16 rounded-r-md 
                      bg-black/15 hover:bg-black/25 text-white transition-all duration-300 
                      focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D97706] 
                      disabled:opacity-30 disabled:pointer-events-none"
                    aria-label="Previous slide"
                  >
                    <ChevronLeft className="w-5 h-5 stroke-[2.5]" />
                  </button>

                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleNext();
                    }}
                    disabled={isEnd}
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 
                      hidden md:flex items-center justify-center w-8 h-16 rounded-l-md 
                      bg-black/15 hover:bg-black/25 text-white transition-all duration-300 
                      focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D97706] 
                      disabled:opacity-30 disabled:pointer-events-none"
                    aria-label="Next slide"
                  >
                    <ChevronRight className="w-5 h-5 stroke-[2.5]" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <QuickViewModal
        isOpen={isQuickViewOpen}
        onClose={() => setIsQuickViewOpen(false)}
        slug={selectedProduct?.slug}
      />
    </section>
  );
};

export default TopSellingWithBanner;
